import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { callAPI } from "../../../../functions/api";
import { CopyBlock, dracula } from "react-code-blocks";

// Helper function to format camelCase model names to readable format
const formatModelName = (name) => {
  if (!name) return name;

  return (
    name
      // Insert space before uppercase letters that follow lowercase letters
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Insert space after numbers that are followed by letters
      .replace(/(\d)([a-zA-Z])/g, "$1 $2")
      // Handle consecutive uppercase letters followed by lowercase
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
  );
};

export const modelNodeModal = ({ open, node }) => {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");

  // Sync selected model from node output when modal opens (but not when node data changes due to our own updates)
  useEffect(() => {
    if (open) {
      const defaultModel = node?.data?.output?.selectedModel || "";
      const defaultSchema = node?.data?.output?.selectedSchema || "";

      // Only sync if the current selectedModel is different from what's in the node
      // This prevents the cycle where we update the node, then the node updates us back
      if (selectedModel !== defaultModel) {
        setSelectedModel(defaultModel);
        setSelectedSchema(defaultSchema);
      }
    }
  }, [open]); // Remove 'node' from dependencies to prevent the cycle

  // Fetch models
  useEffect(() => {
    const fetchModels = async () => {
      const res = await callAPI("admin/models", "GET", null, true);
      setModels(res || []);
    };
    fetchModels();
  }, []);

  // Display selected model JSON
  const memoizedJson = useMemo(() => {
    if (!selectedSchema || selectedSchema == "") return "No model selected";
    return JSON.stringify(selectedSchema, null, 2);
  }, [selectedSchema]); // Only depend on selectedSchema, not models

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: "14px", marginBottom: "5px" }}>Select Model</Typography>
        <FormControl size="small">
          <Select
            sx={{
              backgroundColor: "#2E2E2E",
              fontSize: "12px",
              height: "30px",
            }}
            value={selectedModel}
            onChange={(e) => {
              const val = e.target.value;
              const schema = models.find((m) => m.name === val);
              setSelectedModel(val);
              setSelectedSchema(schema || "");

              if (node?.data?.setData) {
                node.data.setData((oldData) => ({
                  ...oldData,
                  output: { ...oldData.output, selectedModel: val, selectedSchema: schema || "" },
                }));
              }
            }}
          >
            {models.map((model) => (
              <MenuItem key={model.name} value={model.name}>
                {model.description || formatModelName(model.name)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Typography sx={{ fontSize: "14px", marginBottom: "5px" }}>
          Selected Model: {selectedModel ? formatModelName(selectedModel) : "None"}
        </Typography>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "#1e1e1e",
            borderRadius: "6px",
            border: "1px solid #444",
            padding: "10px",
            minHeight: 0,
          }}
        >
          <CopyBlock
            text={memoizedJson}
            language="json"
            theme={dracula}
            showLineNumbers={false}
            wrapLongLines={true}
            codeBlock={{
              copyText: "Copy",
              copySuccessText: "Copied!",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
